import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import {
  KANALNAME,
  KUNDENARTNAME,
  type Kanal,
  type Kunde,
  type Kundenart,
  type Kundenvorschau as Vorschau,
  type Kundenzeile,
  type Vorschaukanal,
  vorschau as ladeVorschau,
} from '../api/kunden';
import { alsEuro } from '../betrag';
import { alsTag, alsZeitpunkt } from '../datum';
import { Feldraster, Feldzeile } from '../komponenten/Akte';

/**
 * Wie lange der Zeiger auf einer Zeile ruhen muss, bevor die Vorschau fragt.
 *
 * Wer mit der Maus ueber die Liste faehrt, streift in einer Sekunde zehn
 * Zeilen. Jede davon zu laden hiesse zehn Anfragen, von denen neun niemand
 * liest. 200 ms sind kuerzer, als man zum Lesen einer Zeile braucht.
 */
export const VORSCHAU_RUHE_MS = 200;

/** Wie lange eine geladene Vorschau gilt; danach wird sie beim naechsten Ueberfahren neu gefragt. */
const GILT_MS = 60_000;

export type Vorschauzustand =
  | { readonly art: 'laedt' }
  | { readonly art: 'da'; readonly daten: Vorschau }
  | { readonly art: 'fehler' };

/**
 * Die Vorschau zu EINER Zeile — entprellt und zwischengespeichert.
 *
 *   * ENTPRELLT: Gefragt wird erst, wenn der Zeiger `VORSCHAU_RUHE_MS` auf
 *     derselben Zeile ruht. Schnelles Ueberfahren loest keine Anfrage aus.
 *   * ZWISCHENGESPEICHERT: Eine Zeile, die schon geladen ist, steht beim
 *     zweiten Ueberfahren sofort da. Der Speicher gehoert zur Liste — eine
 *     neue Liste (anderer Einstieg) fragt neu.
 *   * KEINE DOPPELTE ANFRAGE: Kehrt der Zeiger zurueck, waehrend die Anfrage
 *     noch laeuft, wird auf dieselbe gewartet.
 *   * KEIN SPAETER UEBERSCHREIBER: Eine Antwort fuer eine Zeile, die nicht
 *     mehr gemeint ist, landet im Speicher, aber nicht auf dem Bildschirm.
 */
export function useKundenvorschau(id: string): Vorschauzustand | undefined {
  const ablage = useRef(new Map<string, { readonly daten: Vorschau; readonly zeit: number }>());
  const unterwegs = useRef(new Map<string, Promise<Vorschau>>());
  const [stand, setzeStand] = useState<{ readonly id: string; readonly zustand: Vorschauzustand } | undefined>(
    undefined,
  );

  useEffect(() => {
    const gemerkt = ablage.current.get(id);
    if (gemerkt !== undefined && Date.now() - gemerkt.zeit < GILT_MS) {
      setzeStand({ id, zustand: { art: 'da', daten: gemerkt.daten } });
      return undefined;
    }
    let gemeint = true;
    setzeStand({ id, zustand: { art: 'laedt' } });
    const kennung = setTimeout(() => {
      const anfrage = unterwegs.current.get(id) ?? ladeVorschau(id);
      unterwegs.current.set(id, anfrage);
      void anfrage
        .then(
          (daten) => {
            ablage.current.set(id, { daten, zeit: Date.now() });
            if (gemeint) setzeStand({ id, zustand: { art: 'da', daten } });
          },
          () => {
            if (gemeint) setzeStand({ id, zustand: { art: 'fehler' } });
          },
        )
        .finally(() => unterwegs.current.delete(id));
    }, VORSCHAU_RUHE_MS);
    return () => {
      gemeint = false;
      clearTimeout(kennung);
    };
  }, [id]);

  return stand?.id === id ? stand.zustand : undefined;
}

const BUCHUNGSSTATUS: Readonly<Record<NonNullable<NonNullable<Vorschau['buchungen']>['letzte']>['status'], string>> = {
  gebucht: 'gebucht',
  umgebucht: 'umgebucht',
  storniert: 'storniert',
  teilstorniert: 'teilstorniert',
};

/* BUC-98: das Ergebnis der letzten Pruefung zur hinterlegten Nummer. */
const MITGLIEDSCHAFT: Readonly<Record<NonNullable<Vorschau['mitgliedschaft']> | 'ungeprueft', string>> = {
  mitglied: 'Mitglied (geprüft)',
  kein_mitglied: 'kein Mitglied laut Prüfung',
  nicht_erreichbar: 'Prüfung nicht erreichbar',
  ungeprueft: 'noch nicht geprüft',
};

/* KUN-19, LEG-12: ohne dokumentierte Erlaubnis wird nicht geworben. */
const WERBUNG: Readonly<Record<NonNullable<Vorschau['werbeerlaubnis']> | 'keine', string>> = {
  einwilligung: 'ja, Einwilligung',
  ausnahme: 'ja, Bestandskundenausnahme',
  widerspruch: 'nein, Widerspruch',
  keine: 'nein, keine dokumentiert',
};

function tag(wert: string): string {
  return alsTag(wert) ?? wert;
}

function mehrzahl(anzahl: number, eins: string, mehrere: string): string {
  return `${String(anzahl)} ${anzahl === 1 ? eins : mehrere}`;
}

/** KUN-04: anklickbar — `tel:` ohne Leerzeichen und Striche, `mailto:` wie hinterlegt. */
export function Kanalliste({ kanaele }: { readonly kanaele: readonly Vorschaukanal[] }): JSX.Element {
  return (
    <ul className="vorschau__kanaele">
      {kanaele.map((k) => (
        <li key={`${k.art}-${k.wert}`}>
          <span className="vorschau__kanalart">{KANALNAME[k.art]}</span>{' '}
          <a href={k.art === 'email' ? `mailto:${k.wert}` : `tel:${k.wert.replace(/[^\d+]/g, '')}`}>{k.wert}</a>
        </li>
      ))}
    </ul>
  );
}

function Abschnitt({
  titel,
  hinweis,
  children,
}: {
  readonly titel: string;
  readonly hinweis?: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section className="vorschau__abschnitt" aria-label={titel}>
      <h2>{titel}</h2>
      <Feldraster>{children}</Feldraster>
      {hinweis !== undefined && <p className="feld__hilfe">{hinweis}</p>}
    </section>
  );
}

/** Telefon, Mobil und E-Mail in der Reihenfolge der Vorschau: der primaere vorn, dann nach Art und Wert. */
const KANALFOLGE: readonly Vorschaukanal['art'][] = ['telefon', 'mobil', 'email'];

function vorschaukanaele(kanaele: readonly Kanal[]): Vorschaukanal[] {
  return kanaele
    .flatMap((k): Vorschaukanal[] =>
      k.art === 'website' ? [] : [{ art: k.art, wert: k.wert, istPrimaer: k.istPrimaer }],
    )
    .sort(
      (a, b) =>
        Number(b.istPrimaer) - Number(a.istPrimaer)
        || KANALFOLGE.indexOf(a.art) - KANALFOLGE.indexOf(b.art)
        || a.wert.localeCompare(b.wert),
    );
}

/**
 * DER KONTAKTBLOCK DER VORSCHAU AUS DER GELADENEN AKTE (Auftrag CRM-56).
 *
 * Die Kontaktkarte am Namen im Kopf der Akte braucht keine eigene Anfrage:
 * Was die Akte geladen hat, darf der Angemeldete auch sehen, und die Karte
 * steht beim Öffnen sofort da. Gewählt wird wie in `GET /kunden/:id/vorschau`
 * (`kunde.repository.ts`), damit Liste und Akte denselben Kontakt nennen.
 */
export function kontaktAusAkte(kunde: Kunde): Vorschau['kontakt'] {
  /* KUN-03: die Standardadresse, die Rechnungsadresse vor den anderen. */
  const adresse = [...kunde.adressen].sort(
    (a, b) => Number(b.standard) - Number(a.standard) || Number(b.typ === 'rechnung') - Number(a.typ === 'rechnung'),
  )[0];
  /* KUN-08: nur Firmen und oeffentliche Auftraggeber; die Rolle „Ansprechpartner" vor den uebrigen. */
  const dabei =
    kunde.kundenart === 'privat'
      ? []
      : kunde.kontakte
          .filter((k) => !k.ausgeschieden)
          .sort(
            (a, b) =>
              Number(b.rolle === 'ansprechpartner') - Number(a.rolle === 'ansprechpartner')
              || Number(b.rechnungsempfaenger) - Number(a.rechnungsempfaenger)
              || a.person.nachname.localeCompare(b.person.nachname)
              || (a.person.vorname ?? '').localeCompare(b.person.vorname ?? ''),
          );
  const erster = dabei[0];
  return {
    adresse:
      adresse === undefined
        ? null
        : { zusatz: adresse.zusatz, strasse: adresse.strasse, plz: adresse.plz, ort: adresse.ort, land: adresse.land },
    kanaele: vorschaukanaele(kunde.kanaele),
    ansprechpartner:
      erster === undefined
        ? null
        : {
            name: [erster.person.titel, erster.person.vorname, erster.person.nachname]
              .filter((teil): teil is string => teil !== null && teil !== '')
              .join(' '),
            funktion: erster.funktion,
            kanaele: vorschaukanaele(erster.kanaele),
          },
    weitereAnsprechpartner: Math.max(dabei.length - 1, 0),
  };
}

/**
 * Anschrift, Telefon und E-Mail, bei Firmen der Ansprechpartner — in der
 * Vorschau der Liste und in der Kontaktkarte am Namen der Akte (CRM-56).
 */
export function Kontakt({
  kontakt,
  kundenart,
}: {
  readonly kontakt: Vorschau['kontakt'];
  readonly kundenart: Kundenart;
}): JSX.Element {
  const { adresse, kanaele, ansprechpartner, weitereAnsprechpartner } = kontakt;
  return (
    <Abschnitt titel="Kontakt">
      {/* KUN-03: die Standardadresse, die Rechnungsadresse vorn. */}
      <Feldzeile
        beschriftung="Anschrift"
        wert={adresse === null ? 'keine Anschrift hinterlegt' : null}
        anzeige={
          adresse === null ? undefined : (
            <>
              {adresse.zusatz !== null && (
                <>
                  {adresse.zusatz}
                  <br />
                </>
              )}
              {adresse.strasse}
              <br />
              {`${adresse.plz} ${adresse.ort}${adresse.land === 'DE' ? '' : `, ${adresse.land}`}`}
            </>
          )
        }
      />
      <Feldzeile
        beschriftung="Erreichbar"
        wert={kanaele.length === 0 ? 'kein Telefon und keine E-Mail hinterlegt' : null}
        anzeige={kanaele.length === 0 ? undefined : <Kanalliste kanaele={kanaele} />}
      />
      {/* KUN-08: nur Firmen und oeffentliche Auftraggeber fuehren Ansprechpartner. */}
      {kundenart !== 'privat' && (
        <Feldzeile
          beschriftung="Ansprechpartner"
          wert={ansprechpartner === null ? 'kein Ansprechpartner hinterlegt' : null}
          anzeige={
            ansprechpartner === null ? undefined : (
              <>
                {ansprechpartner.name}
                {ansprechpartner.funktion !== null && `, ${ansprechpartner.funktion}`}
                {ansprechpartner.kanaele.length > 0 && <Kanalliste kanaele={ansprechpartner.kanaele} />}
                {weitereAnsprechpartner > 0 && (
                  <small className="vorschau__nachsatz">
                    {` und ${mehrzahl(weitereAnsprechpartner, 'weiterer', 'weitere')} in der Akte`}
                  </small>
                )}
              </>
            )
          }
        />
      )}
    </Abschnitt>
  );
}

/**
 * AUS-01, AUS-06: die Kennzahlen des Katalogs, nur mit dem Recht des Berichts.
 * AUS-10: Der laufende Zeitraum traegt sein Ende — „bis heute" ist kein Jahr.
 */
function Umsatz({ umsatz }: { readonly umsatz: NonNullable<Vorschau['kennzahlen']['umsatz']> }): JSX.Element {
  const { laufendesJahr, vorjahr, offen } = umsatz;
  return (
    <Abschnitt
      titel="Umsatz"
      hinweis="Netto nach Belegdatum, Stornos und Gutschriften verrechnet — wie der Bericht „Umsatz netto“."
    >
      <Feldzeile
        beschriftung={`Netto ${laufendesJahr.von.slice(0, 4)} bis ${tag(laufendesJahr.bis)}`}
        wert={alsEuro(laufendesJahr.netto)}
      />
      <Feldzeile beschriftung={`Netto ${vorjahr.von.slice(0, 4)}`} wert={alsEuro(vorjahr.netto)} />
      {/* AUS-04, RNG-24: dieselben Posten wie die Liste der offenen Posten. */}
      <Feldzeile
        beschriftung="Offen, brutto"
        wert={
          offen.anzahl === 0
            ? 'keine offenen Posten'
            : `${alsEuro(offen.summe)} aus ${mehrzahl(offen.anzahl, 'Beleg', 'Belegen')}`
        }
      />
    </Abschnitt>
  );
}

function Buchungen({ buchungen }: { readonly buchungen: NonNullable<Vorschau['buchungen']> }): JSX.Element {
  const { letzte, naechste } = buchungen;
  return (
    <Abschnitt titel="Buchungen">
      <Feldzeile
        beschriftung="Letzte Buchung"
        wert={letzte === null ? 'noch keine Buchung' : null}
        anzeige={
          letzte === null
            ? undefined
            : `${tag(letzte.gebuchtAm)} · ${letzte.training} · ${BUCHUNGSSTATUS[letzte.status]}`
        }
      />
      <Feldzeile
        beschriftung="Nächster Termin"
        wert={naechste === null ? 'kein Termin gebucht' : null}
        anzeige={
          naechste === null
            ? undefined
            : `${alsZeitpunkt(naechste.beginn) ?? naechste.beginn} · ${naechste.training}${
                naechste.plaetze > 1 ? ` · ${String(naechste.plaetze)} Plätze` : ''
              }`
        }
      />
      <Feldzeile
        beschriftung="Buchungen"
        wert={`${String(buchungen.anzahl)} gesamt, ${String(buchungen.anzahl12Monate)} in 12 Monaten`}
      />
      <Feldzeile
        beschriftung="Teilnehmende"
        wert={`${String(buchungen.teilnehmende)} gesamt, ${String(buchungen.teilnehmende12Monate)} in 12 Monaten`}
      />
    </Abschnitt>
  );
}

function Weiteres({ daten }: { readonly daten: Vorschau }): JSX.Element {
  const { angebote, auftraege } = daten.kennzahlen;
  const erste = daten.buchungen?.ersteAm ?? null;
  const seitBuchung = erste !== null && erste < daten.angelegtAm;
  const vorgang = (summe: { readonly anzahl: number; readonly summe: string }, keine: string): string =>
    summe.anzahl === 0 ? keine : `${String(summe.anzahl)} · ${alsEuro(summe.summe)} netto`;
  return (
    <Abschnitt titel="Weiteres">
      {/* M-10: die offenen Vorgaenge — Kennzahlen „Offenes Angebotsvolumen" und „Auftragsbestand" (AUS-01). */}
      {angebote !== null && <Feldzeile beschriftung="Offene Angebote" wert={vorgang(angebote, 'keine')} />}
      {auftraege !== null && <Feldzeile beschriftung="Offene Aufträge" wert={vorgang(auftraege, 'keine')} />}
      {/* GUT-01: gueltig, nicht eingeloest, mit Restwert. */}
      {daten.gutscheine !== null && (
        <Feldzeile
          beschriftung="Gutscheine"
          wert={
            daten.gutscheine.anzahl === 0
              ? 'keine gültigen'
              : `${mehrzahl(daten.gutscheine.anzahl, 'gültiger', 'gültige')} · Restwert ${alsEuro(daten.gutscheine.restwert)}`
          }
        />
      )}
      {daten.mitgliedsnummer !== null && (
        <Feldzeile
          beschriftung="Mitgliedschaft"
          wert={`${daten.mitgliedsnummer} · ${MITGLIEDSCHAFT[daten.mitgliedschaft ?? 'ungeprueft']}`}
        />
      )}
      {/* KUN-10: Rabattgruppe und Zahlungsziel aus den Konditionen. */}
      {daten.rabattgruppe !== null && <Feldzeile beschriftung="Rabattgruppe" wert={daten.rabattgruppe} />}
      {daten.zahlungsziel !== null && (
        <Feldzeile beschriftung="Zahlungsziel" wert={mehrzahl(daten.zahlungsziel, 'Tag', 'Tage')} />
      )}
      <Feldzeile beschriftung="Werbung" wert={WERBUNG[daten.werbeerlaubnis ?? 'keine']} />
      <Feldzeile
        beschriftung="Kunde seit"
        wert={
          seitBuchung && erste !== null
            ? `${tag(erste)} (erste Buchung)`
            : `${tag(daten.angelegtAm)} (angelegt)`
        }
      />
    </Abschnitt>
  );
}

/**
 * Die Vorschau der Kundenliste (Pflichtenheft M-10 i. d. F. AeA-45).
 *
 * Kopf und Kundenart stehen sofort da — sie kommen aus der Listenzeile. Die
 * Eckdaten folgen, sobald der Zeiger ruht. Jeder Block, den der Benutzer
 * nicht sehen darf, FEHLT; jeder leere Block sagt, dass er leer ist.
 */
export function Kundenvorschau({
  zeile,
  beiOeffnen,
}: {
  readonly zeile: Kundenzeile;
  readonly beiOeffnen: (id: string) => void;
}): JSX.Element {
  const zustand = useKundenvorschau(zeile.id);
  const daten = zustand?.art === 'da' ? zustand.daten : undefined;

  return (
    <>
      <div className="karte__titel">
        {zeile.name}
        <small>{zeile.kundennummer}</small>
      </div>
      {/* KUN-17: gesperrte Kunden tragen ihr Kennzeichen, mit Grund, sobald er da ist. */}
      {zeile.gesperrt && (
        <p className="warnung">
          {daten?.sperrgrund != null ? `Gesperrt: ${daten.sperrgrund}` : 'Dieser Kunde ist gesperrt.'}
        </p>
      )}
      <Feldraster>
        <Feldzeile beschriftung="Kundenart" wert={KUNDENARTNAME[zeile.kundenart]} />
        <Feldzeile beschriftung="Betreuer" wert={zeile.betreuerName} />
      </Feldraster>
      {(zustand === undefined || zustand.art === 'laedt') && (
        <p className="feld__hilfe" role="status">
          Eckdaten werden geladen …
        </p>
      )}
      {zustand?.art === 'fehler' && (
        <p className="warnung" role="status">
          Die Eckdaten ließen sich nicht laden. Die Akte zeigt sie vollständig.
        </p>
      )}
      {daten !== undefined && (
        <>
          <Kontakt kontakt={daten.kontakt} kundenart={daten.kundenart} />
          {daten.kennzahlen.umsatz !== null && <Umsatz umsatz={daten.kennzahlen.umsatz} />}
          {daten.buchungen !== null && <Buchungen buchungen={daten.buchungen} />}
          <Weiteres daten={daten} />
        </>
      )}
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={() => beiOeffnen(zeile.id)}>
          Akte öffnen
        </button>
      </div>
    </>
  );
}
